import { type FC, type ReactNode, useState } from 'react';
import { useIntl, defineMessages, FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import classnames from 'classnames';
import { constants } from 'ethers';
import { useFragment, graphql } from 'react-relay';
import CopyLink from 'dibs-copy-text';
import { useSplit } from 'dibs-crypto-utils/exports/useSplit';
import { type Allocation } from 'dibs-crypto-utils/exports/useSplit';
import {
    normalizeAddress,
    shortAddress,
    etherscanUrl,
    shortName,
} from 'dibs-crypto-utils/exports/formattingUtils';
import { usePersistedWalletAddress } from 'dibs-crypto-utils/exports/usePersistedWalletAddress';
import { useIsPrimarySale } from 'dibs-crypto-utils/exports/useIsPrimarySale';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { Link } from 'dibs-elements/exports/Link';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { getSplitLink } from '../../../helpers/getSplitLink';
import { SplitModalHeader } from '../components/SplitModalHeader';

import { type SplitDetailsContent_address$key } from './__generated__/SplitDetailsContent_address.graphql';
import { type SplitDetailsContent_paymentAddresses$key } from './__generated__/SplitDetailsContent_paymentAddresses.graphql';
import { type SplitDetailsContent_viewer$key } from './__generated__/SplitDetailsContent_viewer.graphql';
import { type SplitDetailsContent_nftItem$key } from './__generated__/SplitDetailsContent_nftItem.graphql';

import styles from '../styles/SplitDetailsContent.scss';

const messages = defineMessages({
    percentageEarnings: {
        id: 'nft.splitModal.percentageEarnings',
        defaultMessage: '{percentage}% earnings',
    },
});

const AllocationRow: FC<{ allocationObj: Allocation; persistedAddress: string }> = ({
    allocationObj,
    persistedAddress,
}) => {
    const { address: addressObj, allocation, basisPoints } = allocationObj;
    const { address, ensDomain, publicProfile } = addressObj || {};
    const isLoggedInUser = normalizeAddress(persistedAddress) === normalizeAddress(address || '');
    let name: string = '';
    if (publicProfile?.displayName) {
        name = `@${publicProfile.displayName}`;
    } else if (ensDomain) {
        name = ensDomain;
    } else if (address) {
        name = shortAddress(address);
    }
    return (
        <li className={styles.recipientWrapper} key={address}>
            <Link
                href={
                    publicProfile
                        ? `/nft/address/ethereum/${address}`
                        : etherscanUrl(`/address/${address}`)
                }
                className={styles.linkWrapper}
                buyerLinkType="linkNoDecoration"
            >
                {/* image / gradient */}
                <NftAvatarCircle
                    addressOverride={{
                        address: addressObj?.address || '',
                        avatarImage: addressObj?.publicProfile?.avatarImage?.path || '',
                    }}
                    wrapperClassName={styles.avatarWrapper}
                />
                {/* display name */}
                <div>
                    {isLoggedInUser && (
                        <span className={styles.youText}>
                            <FormattedMessage
                                id="nft.splitModal.details.you"
                                defaultMessage="<b>You</b>"
                                values={{
                                    b: chunks => <span className={styles.boldText}>{chunks}</span>,
                                }}
                            />
                        </span>
                    )}
                    <span>{name}</span>
                </div>
            </Link>
            {/* percentage */}
            <span className={styles.percentage}>
                <FormattedNumber
                    style="percent"
                    value={(allocation || 0) / (basisPoints || 10000)}
                    maximumFractionDigits={2}
                />
            </span>
        </li>
    );
};

const AllocationList: FC<{
    allocations: Allocation[];
    persistedAddress: string;
    splitAddress?: string | null;
    splitEns?: string | null;
    earningsPercentage?: number | null;
    isOxSplit: boolean;
    splitName?: string | null;
    showSplitNameAddress?: boolean;
}> = ({
    allocations,
    persistedAddress,
    splitAddress,
    splitEns,
    earningsPercentage,
    isOxSplit,
    splitName,
    showSplitNameAddress = false,
}) => {
    const { formatMessage } = useIntl();
    return (
        <div>
            <div className={classnames(styles.splitInfoContainer, styles.inBody)}>
                {earningsPercentage && (
                    <span className={styles.earningsPill}>
                        {formatMessage(messages.percentageEarnings, {
                            percentage: earningsPercentage,
                        })}
                    </span>
                )}
                {showSplitNameAddress && isOxSplit && (
                    <div className={styles.splitNameAddressContainer}>
                        {splitName && <span className={styles.splitName}>{splitName}</span>}
                        <div className={styles.addressContainer}>
                            <Link href={getSplitLink(splitAddress || '')} target="_blank">
                                {shortName(splitEns || '') || shortAddress(splitAddress || '')}
                            </Link>
                            <CopyLink
                                textToCopy={splitAddress || ''}
                                copyNoir
                                hideLink
                                tooltipOptions={{
                                    direction: 'bottom',
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            <FormattedMessage
                id="splitModal.details.bodyText"
                defaultMessage="Funds will be distributed to these recipients."
            />{' '}
            {splitAddress && isOxSplit && (
                <Link href={getSplitLink(splitAddress)} target="_blank">
                    <FormattedMessage
                        id="splitModal.details.bodyText.splitLink"
                        defaultMessage="View additional details on 0xSplits."
                    />
                </Link>
            )}
            <ul className={styles.recipientList}>
                {allocations.filter(filterNulls).map(allocationObj => (
                    <AllocationRow
                        key={allocationObj?.address?.address}
                        allocationObj={allocationObj}
                        persistedAddress={persistedAddress}
                    />
                ))}
            </ul>
        </div>
    );
};

const SplitMetadataCard: FC<{ header: ReactNode; info: ReactNode; value: ReactNode }> = ({
    header,
    info,
    value,
}) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);

    return (
        <div className={styles.splitMetadataCard}>
            <div className={styles.splitMetadataCardHeader}>
                <span>{header}</span>
                <div className={styles.infoContainer}>
                    <Link
                        onClick={() => setTooltipVisible(!tooltipVisible)}
                        className={styles.infoCircleContainer}
                    >
                        <InfoCircle className={styles.infoCircle} />
                    </Link>
                    <Tooltip
                        zIndex="low"
                        isVisible={tooltipVisible}
                        closeOnOutsideClick
                        onClose={() => setTooltipVisible(false)}
                        align="right"
                        direction="right"
                    >
                        {info}
                    </Tooltip>
                </div>
            </div>
            {value}
        </div>
    );
};

const BASIS_POINTS = 1_000_000;

const SplitMetadata: FC<{
    distributorFee: string | null | undefined;
    controllerAddress: string | null | undefined;
    controllerEns: string | null | undefined;
}> = ({ distributorFee, controllerAddress, controllerEns }) => {
    return (
        <div className={styles.splitMetadata}>
            <SplitMetadataCard
                header={
                    <FormattedMessage
                        id="splitModal.details.splitMetadata.distributorFee.header"
                        defaultMessage="Distributor Fee"
                    />
                }
                info={
                    <FormattedMessage
                        id="splitModal.details.splitMetadata.distributorFee.info"
                        defaultMessage="The percentage amount of earnings that all recipients pay to the party that distributes funds"
                    />
                }
                value={
                    !parseInt(distributorFee || '0') ? (
                        <FormattedMessage
                            id="splitModal.details.splitMetadata.distributorFee.value"
                            defaultMessage="None"
                        />
                    ) : (
                        <FormattedNumber
                            maximumFractionDigits={2}
                            style="percent"
                            value={parseInt(distributorFee || '0') / BASIS_POINTS}
                        />
                    )
                }
            />
            <SplitMetadataCard
                header={
                    <FormattedMessage
                        id="splitModal.details.splitMetadata.controller.header"
                        defaultMessage="Mutable"
                    />
                }
                info={
                    <FormattedMessage
                        id="splitModal.details.splitMetadata.controller.info"
                        defaultMessage="Mutable splits are splits that can be changed once created. Only the controller of the split will be able to edit the split"
                    />
                }
                value={
                    controllerAddress && controllerAddress !== constants.AddressZero ? (
                        <FormattedMessage
                            id="splitModal.details.splitMetadata.controller.mutable"
                            defaultMessage="{controller} can edit the split at any time."
                            values={{
                                controller: (
                                    <Link
                                        target="_blank"
                                        href={etherscanUrl(`/address/${controllerAddress}`)}
                                    >
                                        {shortName(controllerEns || '') ||
                                            shortAddress(controllerAddress)}
                                    </Link>
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="splitModal.details.splitMetadata.controller.immutable"
                            defaultMessage="Immutable"
                        />
                    )
                }
            />
        </div>
    );
};

export type SplitType = 'SALE' | 'ROYALTY';

type Props = {
    singleAddress?: SplitDetailsContent_address$key | null | undefined;
    addressList?: SplitDetailsContent_paymentAddresses$key | null | undefined;
    nftItem?: SplitDetailsContent_nftItem$key | null | undefined;
    viewer: SplitDetailsContent_viewer$key;
    initialSplitType?: SplitType;
    earningsPercentageObj?: { saleEarnings: number; royaltyEarnings: number };
    onClose?: () => void;
    onBack?: () => void;
    showSplitMetadata?: boolean;
};

export const SplitDetailsContent: FC<Props> = ({
    singleAddress: singleAddressRef = null,
    addressList: addressListRef = null,
    viewer: viewerRef,
    nftItem: nftItemRef = null,
    initialSplitType,
    earningsPercentageObj,
    onClose,
    onBack,
    showSplitMetadata = false,
}) => {
    const { formatMessage } = useIntl();
    const [activeTab, setActiveTab] = useState<SplitType | undefined>(initialSplitType);
    const viewer = useFragment(
        graphql`
            fragment SplitDetailsContent_viewer on Viewer {
                ...usePersistedWalletAddress_viewer
                    @arguments(fetchUser: $fetchUser, userId: $userId)
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment SplitDetailsContent_nftItem on NftItem {
                ...useIsPrimarySale_nftItem
            }
        `,
        nftItemRef
    );
    const singleAddress = useFragment(
        graphql`
            fragment SplitDetailsContent_address on EthereumAddressType {
                ...useSplit_address
            }
        `,
        singleAddressRef
    );
    const addressList = useFragment(
        graphql`
            fragment SplitDetailsContent_paymentAddresses on PaymentAddresses {
                ...useSplit_paymentAddresses
            }
        `,
        addressListRef
    );

    const isPrimarySale = useIsPrimarySale({ nftItem });
    const { persistedAddress } = usePersistedWalletAddress({ viewer });

    const hasTwoSplits = !!singleAddress && !!addressList;

    const {
        normalizedAllocations,
        paymentAddress: normalizedSplitAddress,
        paymentEns: normalizedSplitEns,
        isOxSplit: normalizedIsOxSplit,
        distributorFee: normalizedDistributorFee,
        controllerAddress: normalizedControllerAddress,
        controllerEns: normalizedControllerEns,
        splitName: normalizedSplitName,
    } = useSplit({
        singleAddress,
        addressList,
    });

    // in the case that both values are available, and we are showing both sale and royalty splits in tabs
    // we make the assumption that the single address is the sale split, and the list is the royalty split
    // as this is how the data is modeled in graphql
    const {
        normalizedAllocations: singleAddressAllocations,
        paymentAddress: singleSplitAddress,
        paymentEns: singleSplitEns,
        isOxSplit: singleIsOxSplit,
        distributorFee: singleDistributorFee,
        controllerAddress: singleControllerAddress,
        controllerEns: singleControllerEns,
        splitName: singleSplitName,
    } = useSplit({ singleAddress });
    const {
        normalizedAllocations: addressListAllocations,
        paymentAddress: listSplitAddress,
        paymentEns: listSplitEns,
        isOxSplit: listIsOxSplit,
        distributorFee: listDistributorFee,
        controllerAddress: listControllerAddress,
        controllerEns: listControllerEns,
        splitName: listSplitName,
    } = useSplit({ addressList });

    const showTabs = hasTwoSplits && !isPrimarySale;

    const earningsPillString = formatMessage(messages.percentageEarnings, {
        percentage:
            activeTab === 'SALE'
                ? earningsPercentageObj?.saleEarnings
                : earningsPercentageObj?.royaltyEarnings,
    });

    let metadataParams;
    if (!showTabs && normalizedIsOxSplit) {
        metadataParams = {
            distributorFee: normalizedDistributorFee,
            controllerAddress: normalizedControllerAddress,
            controllerEns: normalizedControllerEns,
        };
    } else if (activeTab === 'ROYALTY' && singleIsOxSplit) {
        metadataParams = {
            distributorFee: listDistributorFee,
            controllerAddress: listControllerAddress,
            controllerEns: listControllerEns,
        };
    } else if (listIsOxSplit) {
        metadataParams = {
            distributorFee: singleDistributorFee,
            controllerAddress: singleControllerAddress,
            controllerEns: singleControllerEns,
        };
    }

    return (
        <TabProvider
            activeTab={activeTab || 'SALE'}
            setActiveTab={(val: SplitType) => setActiveTab(val)}
        >
            <SplitModalHeader
                onClose={onClose}
                onBack={onBack}
                wrapperClass={showTabs ? styles.hasTabs : undefined}
                title={
                    <FormattedMessage
                        defaultMessage="Split Details"
                        id="nft.advancedSplit.splitDetails.header"
                    />
                }
                subtitle={
                    <>
                        {showTabs && (
                            <div className={styles.tabListContainer}>
                                <TabList>
                                    {[
                                        [
                                            'SALE',
                                            <span
                                                key="split-details-sale-tab"
                                                className={styles.tabLabel}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <FormattedMessage
                                                    id="splitModal.details.tab.sale"
                                                    defaultMessage="Sale"
                                                />
                                            </span>,
                                        ],
                                        [
                                            'ROYALTY',
                                            <span
                                                key="split-details-royalty-tab"
                                                className={styles.tabLabel}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <FormattedMessage
                                                    id="splitModal.details.tab.royalty"
                                                    defaultMessage="Royalty"
                                                />
                                            </span>,
                                        ],
                                    ]}
                                </TabList>
                            </div>
                        )}
                        {!showTabs && (
                            <div className={styles.splitInfoContainer}>
                                {!!earningsPercentageObj && (
                                    <span className={styles.earningsPill}>
                                        {earningsPillString}
                                    </span>
                                )}
                                {normalizedIsOxSplit && (
                                    <div className={styles.splitNameAddressContainer}>
                                        {normalizedSplitName && (
                                            <span className={styles.splitName}>
                                                {normalizedSplitName}
                                            </span>
                                        )}
                                        <div className={styles.addressContainer}>
                                            <Link
                                                href={getSplitLink(normalizedSplitAddress || '')}
                                                target="_blank"
                                            >
                                                {shortName(normalizedSplitEns || '') ||
                                                    shortAddress(normalizedSplitAddress || '')}
                                            </Link>
                                            <CopyLink
                                                textToCopy={normalizedSplitAddress || ''}
                                                copyNoir
                                                hideLink
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                }
            />
            <ModalBody
                className={classnames(styles.modalBody, styles.important, {
                    [styles.splitMetadataLayout]: showSplitMetadata && !!metadataParams,
                })}
            >
                {showSplitMetadata && metadataParams && <SplitMetadata {...metadataParams} />}
                {showTabs ? (
                    <TabPanels>
                        {[
                            [
                                'SALE',
                                <AllocationList
                                    allocations={singleAddressAllocations}
                                    persistedAddress={persistedAddress}
                                    splitAddress={singleSplitAddress}
                                    splitEns={singleSplitEns}
                                    splitName={singleSplitName}
                                    earningsPercentage={earningsPercentageObj?.saleEarnings}
                                    isOxSplit={singleIsOxSplit}
                                    showSplitNameAddress
                                />,
                            ],
                            [
                                'ROYALTY',
                                <AllocationList
                                    allocations={addressListAllocations}
                                    persistedAddress={persistedAddress}
                                    splitEns={listSplitEns}
                                    splitName={listSplitName}
                                    splitAddress={listSplitAddress}
                                    earningsPercentage={earningsPercentageObj?.royaltyEarnings}
                                    isOxSplit={listIsOxSplit}
                                    showSplitNameAddress
                                />,
                            ],
                        ]}
                    </TabPanels>
                ) : (
                    <AllocationList
                        allocations={normalizedAllocations}
                        persistedAddress={persistedAddress}
                        splitAddress={normalizedSplitAddress}
                        isOxSplit={normalizedIsOxSplit}
                    />
                )}
            </ModalBody>
        </TabProvider>
    );
};
